// Calculator Policy Configuration Types
export interface CalculatorBSDTier {
  maxPrice: number;
  rate: number;
  baseAmount: number;
}

export interface CalculatorABSDRate {
  citizenship: string;
  firstProperty: number;
  secondProperty: number;
  thirdPlusProperty: number;
}

export interface CalculatorLTVLimit {
  loanCount: string;
  ltv: number;
}

export interface CalculatorSSDTier {
  minYears: number;
  maxYears: number;
  rate: number;
}

export interface CalculatorSSDRates {
  before2025: CalculatorSSDTier[];
  from2025: CalculatorSSDTier[];
  commercialIndustrial: CalculatorSSDTier[];
}

export interface CalculatorEffectiveDates {
  residential2025Change: string;
}

export interface CalculatorFormLimits {
  interestRate: {
    min: number;
    max: number;
    step: number;
    default: number;
  };
  tenure: {
    min: number;
    max: number;
    default: number;
  };
  propertyPrice: {
    min: number;
    default: number;
  };
}

export interface CalculatorPropertyFees {
  ZERO: { bookingFee: number; exerciseFee: number };
  ONE: { bookingFee: number; exerciseFee: number };
  GREATER_THAN_TWO: { bookingFee: number; exerciseFee: number };
}

export interface CalculatorBUCLTVRate {
  condition: string;
  firstHousingLoan: number;
  secondHousingLoan: number;
  thirdHousingLoan: number;
}

export interface CalculatorPaymentStage {
  name: string;
  loanPercent: number;
}

export interface CalculatorAmortisationDefaults {
  interestRates: {
    year1: number;
    year2: number;
    year3: number;
    thereafter: number;
  };
  loanTenure: number;
  loanAmount: number;
}

export interface CalculatorPolicyConfig {
  version: string;
  lastUpdated: string;
  effectiveDate: string;
  description: string;
  bsdTiers: CalculatorBSDTier[];
  absdRates: CalculatorABSDRate[];
  housingLoanLTV: CalculatorLTVLimit[];
  ssdRates: CalculatorSSDRates;
  effectiveDates: CalculatorEffectiveDates;
  propertyFees: CalculatorPropertyFees;
  bucltvRates: CalculatorBUCLTVRate[];
  paymentStages: CalculatorPaymentStage[];
  formLimits: CalculatorFormLimits;
  amortisationDefaults: CalculatorAmortisationDefaults;
  amortisationInterestRatePeriods: Array<{
    name: string;
    months: number | "unlimited";
    startMonth: number;
  }>;
}

// Default Calculator Configuration
export const defaultCalculatorPolicyConfig: CalculatorPolicyConfig = {
  version: "1.0",
  lastUpdated: "2025-12-16",
  effectiveDate: new Date().toISOString().split("T")[0],
  description: "Singapore Property Tax Policy Configuration",

  bsdTiers: [
    { maxPrice: 180000, rate: 0.01, baseAmount: 0 },
    { maxPrice: 360000, rate: 0.02, baseAmount: 1800 },
    { maxPrice: 1000000, rate: 0.03, baseAmount: 5400 },
    { maxPrice: 1500000, rate: 0.04, baseAmount: 24600 },
    { maxPrice: 3000000, rate: 0.05, baseAmount: 44600 },
    { maxPrice: Infinity, rate: 0.06, baseAmount: 119600 },
  ],

  absdRates: [
    {
      citizenship: "Singaporean",
      firstProperty: 0,
      secondProperty: 20,
      thirdPlusProperty: 30,
    },
    {
      citizenship: "Singapore PR",
      firstProperty: 5,
      secondProperty: 30,
      thirdPlusProperty: 35,
    },
    {
      citizenship: "Foreigner",
      firstProperty: 60,
      secondProperty: 60,
      thirdPlusProperty: 60,
    },
    {
      citizenship: "Corporate",
      firstProperty: 65,
      secondProperty: 65,
      thirdPlusProperty: 65,
    },
  ],

  housingLoanLTV: [
    { loanCount: "0", ltv: 75 },
    { loanCount: "1", ltv: 45 },
    { loanCount: "2+", ltv: 35 },
  ],

  ssdRates: {
    before2025: [
      { minYears: 0, maxYears: 1, rate: 12 },
      { minYears: 1, maxYears: 2, rate: 8 },
      { minYears: 2, maxYears: 3, rate: 4 },
      { minYears: 3, maxYears: 99, rate: 0 },
    ],
    from2025: [
      { minYears: 0, maxYears: 1, rate: 16 },
      { minYears: 1, maxYears: 2, rate: 12 },
      { minYears: 2, maxYears: 3, rate: 8 },
      { minYears: 3, maxYears: 4, rate: 4 },
      { minYears: 4, maxYears: 99, rate: 0 },
    ],
    commercialIndustrial: [
      { minYears: 0, maxYears: 1, rate: 15 },
      { minYears: 1, maxYears: 2, rate: 10 },
      { minYears: 2, maxYears: 3, rate: 5 },
      { minYears: 3, maxYears: 99, rate: 0 },
    ],
  },

  effectiveDates: {
    residential2025Change: "2025-07-04",
  },

  propertyFees: {
    ZERO: { bookingFee: 5, exerciseFee: 15 },
    ONE: { bookingFee: 5, exerciseFee: 15 },
    GREATER_THAN_TWO: { bookingFee: 5, exerciseFee: 15 },
  },

  bucltvRates: [
    {
      condition: "d 30 years",
      firstHousingLoan: 75,
      secondHousingLoan: 45,
      thirdHousingLoan: 35,
    },
    {
      condition: "> 30 years or extends past age 65",
      firstHousingLoan: 55,
      secondHousingLoan: 25,
      thirdHousingLoan: 15,
    },
    {
      condition: "Non-Individual Borrowers",
      firstHousingLoan: 15,
      secondHousingLoan: 15,
      thirdHousingLoan: 15,
    },
  ],

  paymentStages: [
    { name: "Booking Fee", loanPercent: 5 },
    { name: "Exercise Fee", loanPercent: 10 },
    { name: "S&P signing", loanPercent: 5 },
    { name: "Foundation", loanPercent: 5 },
    { name: "RC framework", loanPercent: 5 },
    { name: "Walls completion", loanPercent: 5 },
    { name: "CSC completion", loanPercent: 25 },
    { name: "Key collection", loanPercent: 15 },
  ],

  formLimits: {
    interestRate: { min: 0, max: 10, step: 0.1, default: 3.5 },
    tenure: { min: 10, max: 30, default: 25 },
    propertyPrice: { min: 0, default: 0 },
  },

  amortisationDefaults: {
    interestRates: {
      year1: 2.0,
      year2: 2.0,
      year3: 2.0,
      thereafter: 2.0,
    },
    loanTenure: 30,
    loanAmount: 0,
  },

  amortisationInterestRatePeriods: [
    { name: "Year 1", months: 12, startMonth: 1 },
    { name: "Year 2", months: 12, startMonth: 13 },
    { name: "Year 3", months: 12, startMonth: 25 },
    { name: "Thereafter", months: "unlimited", startMonth: 37 },
  ],
};

// Backward compatibility aliases
export type PolicyConfig = CalculatorPolicyConfig;
export const defaultPolicyConfig = defaultCalculatorPolicyConfig;
